/**
 * Infrastructure Layer - Google Sheets API
 * Google Sheetsエクスポートエンドポイント
 */

import apiClient from '../../utils/apiClient';

/**
 * API設定
 */
const API_ENDPOINTS = {
  EXPORT_OUTSIDE_TIME_RANGE: '/api/medical-reservations/export-outside-time-range',
} as const;

/**
 * API呼び出しオプション
 */
const API_OPTIONS = {
  /** タイムアウト（ミリ秒） */
  timeout: 60000,
  /** Cookie送信を無効化 */
  withCredentials: false,
} as const;

/**
 * エクスポートリクエスト型
 */
export interface ExportRequest {
  readonly data: readonly Record<string, string>[];
  readonly metadata?: {
    readonly original_filename?: string;
    readonly total_records?: number;
    readonly outside_records?: number;
    readonly time_range?: {
      readonly start?: string;
      readonly end?: string;
    };
  };
  readonly debug?: boolean;
}

/**
 * エクスポートレスポンス型
 */
export interface ExportResponse {
  readonly success: boolean;
  readonly data?: {
    readonly sheet_name: string;
    readonly sheet_id: string;
    readonly spreadsheet_url: string;
    readonly rows_written: number;
    readonly write_start_row: number;
    readonly processed_at: string;
    readonly time_pattern?: string;
    readonly time_range?: string;
  };
  readonly message?: string;
  readonly error?: {
    readonly code: string;
    readonly message: string;
    readonly timestamp: string;
    readonly details?: unknown;
  };
}

/**
 * エクスポートエラーコード
 */
export const ExportErrorCode = {
  SPREADSHEET_NOT_FOUND: 'SPREADSHEET_NOT_FOUND',
  EXPORT_NOT_AVAILABLE: 'EXPORT_NOT_AVAILABLE',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  GOOGLE_SHEETS_ERROR: 'GOOGLE_SHEETS_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;

export type ExportErrorCode = typeof ExportErrorCode[keyof typeof ExportErrorCode];

/**
 * 時間外データをGoogle Sheetsにエクスポートする
 * @param data エクスポートするデータ
 * @param metadata メタデータ
 * @param debug デバッグモード
 * @returns エクスポート結果
 */
export const exportOutsideTimeRangeData = async (
  data: readonly Record<string, string>[],
  metadata?: ExportRequest['metadata'],
  debug?: boolean
): Promise<ExportResponse> => {
  const request: ExportRequest = {
    data,
    metadata,
    debug: debug ?? process.env.NODE_ENV === 'development',
  };

  try {
    const response = await apiClient.post<ExportResponse>(
      API_ENDPOINTS.EXPORT_OUTSIDE_TIME_RANGE,
      request,
      API_OPTIONS
    );

    return response.data;
  } catch (error: unknown) {
    // エラーレスポンスの処理
    const axiosError = error as { response?: { data?: ExportResponse; status?: number } };

    if (axiosError.response?.data) {
      return axiosError.response.data;
    }

    // デフォルトエラーレスポンス
    return {
      success: false,
      error: {
        code: ExportErrorCode.UNKNOWN_ERROR,
        message: error instanceof Error ? error.message : 'Google Sheetsへのエクスポートに失敗しました',
        timestamp: new Date().toISOString(),
      },
    };
  }
};

/**
 * エクスポート結果のサマリーを生成する
 * @param response エクスポートレスポンス
 * @returns サマリー文字列
 */
export const generateExportSummary = (response: ExportResponse): string => {
  if (response.success && response.data) {
    const { sheet_name, rows_written, time_range } = response.data;
    return `${sheet_name}シートに${rows_written}件のデータをエクスポートしました${time_range ? `（時間範囲: ${time_range}）` : ''}`;
  }

  if (response.error) {
    switch (response.error.code) {
      case ExportErrorCode.SPREADSHEET_NOT_FOUND:
        return 'スプレッドシートが見つかりません。管理者に連絡してください。';
      case ExportErrorCode.EXPORT_NOT_AVAILABLE:
        return 'テンプレートシートが設定されていません。';
      case ExportErrorCode.PERMISSION_DENIED:
        return 'スプレッドシートへのアクセス権限がありません。';
      case ExportErrorCode.RATE_LIMIT_EXCEEDED:
        return 'API利用制限を超えました。しばらく待ってから再試行してください。';
      case ExportErrorCode.VALIDATION_ERROR:
        return 'データの形式が正しくありません。';
      default:
        return `エクスポートエラー: ${response.error.message}`;
    }
  }

  return '予期しないエラーが発生しました。';
};
